import { XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell } from "recharts";
import { formatINR } from "@/lib/format";
import { TrendingUp, TrendingDown } from "lucide-react";
import { COLORS, VERTICAL_CHART_MARGIN, TOOLTIP_STYLE, inrValue } from "./chartTheme";

function Delta({ pct }) {
  if (pct == null) return <span className="font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">—</span>;
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-xs ${up ? "text-[#2E6F40]" : "text-[#D94F3D]"}`}>
      <Icon className="w-3 h-3" /> {up ? "+" : ""}{pct}%
    </span>
  );
}

const RANGES = [
  { k: "month", l: "Month" },
  { k: "3m", l: "3M" },
  { k: "6m", l: "6M" },
  { k: "ytd", l: "YTD" },
];

export function CategoryDeepDive({ cats, range, setRange }) {
  return (
    <section>
      <div className="flex items-end justify-between border-b border-[#D8D4C7] pb-4 mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565]">Deep dive</div>
          <h3 className="font-serif text-3xl tracking-tight">Categories.</h3>
        </div>
        <div className="flex gap-1 border border-[#1A1F16]">
          {RANGES.map((r) => (
            <button
              key={r.k}
              data-testid={`reports-range-${r.k}`}
              onClick={() => setRange(r.k)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-widest ${range === r.k ? "bg-[#1A1F16] text-[#F7F5F0]" : "text-[#1A1F16] hover:bg-[#EAE7DD]"}`}
            >{r.l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-[#D8D4C7] bg-white p-6">
          <div className="h-72" data-testid="chart-category-report">
            {cats.length === 0 ? (
              <div className="h-full flex items-center justify-center text-[#6C7565] text-sm">No data.</div>
            ) : (
              <ResponsiveContainer>
                <BarChart data={cats.slice(0, 8)} layout="vertical" margin={VERTICAL_CHART_MARGIN}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="category" stroke="#1A1F16" fontSize={11} tickLine={false} axisLine={false} width={100} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={inrValue} />
                  <Bar dataKey="current">
                    {cats.slice(0, 8).map((c, i) => <Cell key={c.category} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="border border-[#D8D4C7] bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="category-comparison-table">
            <thead>
              <tr className="border-b border-[#1A1F16] text-left">
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565]">Category</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565] text-right">Current</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565] text-right">Previous</th>
                <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#6C7565] text-right">Δ</th>
              </tr>
            </thead>
            <tbody>
              {cats.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-[#6C7565]">No data.</td></tr>
              ) : cats.map((c) => (
                <tr key={c.category} className="border-b border-[#EAE7DD]">
                  <td className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider">{c.category}</td>
                  <td className="px-4 py-3 text-right num">{formatINR(c.current)}</td>
                  <td className="px-4 py-3 text-right num text-[#6C7565]">{formatINR(c.previous)}</td>
                  <td className="px-4 py-3 text-right"><Delta pct={c.delta_pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
